/**
 * Shared API types and interfaces
 */

// Candidate interfaces
export interface Candidate {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
}

// Bonus interfaces
export interface Bonus {
  type: string;
  name?: string;
  amount: number;
  amount_type?: 'flat' | 'percentage';
  currency?: string;
  frequency?: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  description?: string;
}

// Commission interfaces
export interface CommissionTier {
  threshold: number;
  rate: number;
  is_incremental: boolean;
}

/**
 * Shared by the write path (`OfferSubmitData`) and the read path (`OfferResponse`),
 * so the optional fields have to describe BOTH directions. Writes omit blank
 * fields; reads always emit the key and send `null` when the column is NULL
 * (see `OfferReadSerializer.get_commission`). The `| null` is therefore load
 * bearing — without it `x !== undefined` narrows a `null` to `number` and the
 * compiler blesses `null.toString()`.
 */
export interface Commission {
  type: 'percentage' | 'tiered' | 'quota_ote' | 'draw' | 'hybrid';
  target_amount: number;
  /** NULL for `draw`/`hybrid` (never collected) and whenever the split rate is blank. */
  base_rate?: number | null;
  /** NULL for every type but `quota_ote`, and for `quota_ote` with a blank quota. */
  quota_amount?: number | null;
  payout_frequency: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  has_accelerators: boolean;
  show_performance_scenarios?: boolean;
  details?: string | null;
  tiers?: CommissionTier[];
}

// Employment type interfaces
export type EmploymentType = 'full_time' | 'part_time' | 'contractor';
export type CompensationType = 'salary' | 'hourly';

// Equity interfaces
export interface EquityDetails {
  years_vesting: number;
  number_of_shares: number;
  strike_price: number;
  preferred_price: number;
  outstanding_shares?: number;
  company_valuation?: number;
  cliff_period_months: number;
  equity_type: 'stock_options' | 'rsus';
  show_ownership?: boolean;
  display_currency?: string;
  usd_conversion_rate?: number;
}

// Benefits interfaces
export interface Benefit {
  name: string;
  type: string;
  description?: string;
  url?: string;
  pdf_file?: string;
  pdf_filename?: string;
  pdf_mimetype?: string;
  monetary_value?: number;
  currency?: string;
  frequency?: string;
  derived_from_benefit_template?: string;
}

// Funding Round interface
export interface FundingRound {
  id: string;
  round_name: string;
  round_type: 'historical' | 'current' | 'scenario';
  valuation: number;
  round_date?: string | null;
  dilution_percent: number;
  is_current: boolean;
  display_order: number;
  compounded_dilution_from_current?: number;
}

// Company interfaces
export interface CompanyInfo {
  id?: string | null;
  name: string;
  logo: string;
  header_image: string;
  primary_color: string;
  description: string;
  valuation?: number;
  show_valuation?: boolean;
  show_comparable_companies?: boolean;
  include_benefits_in_comp_chart?: boolean;
  use_gross_equity_value?: boolean;
  /** Upper bound of the comp chart's growth-projection slider (manual moves only). */
  comp_chart_max_multiplier?: number;
  /** Custom disclaimer under the comp chart; empty means the standard copy. */
  comp_chart_disclaimer?: string;
  /** Company-wide welcome message, used when an older offer carries none. */
  default_welcome_message?: string;
  outstanding_shares?: number;
  comparable_companies?: ComparableCompany[];
  funding_rounds?: FundingRound[];
  // Public offer visibility settings
  public_offer_show_compensation?: boolean;
  public_offer_show_equity?: boolean;
  public_offer_show_benefits?: boolean;
  public_offer_show_bonus?: boolean;
  public_offer_show_commission?: boolean;
  public_offer_show_total_comp_chart?: boolean;
  public_offer_show_comparable_companies?: boolean;
  // "Powered by ClearOffer" viral-loop badge visibility (defaults to showing).
  show_powered_by_badge?: boolean;
}

export interface ComparableCompany {
  id: string;
  name: string;
  stage: string;
  valuation: number;
  valuation_date?: string | null;
  private_notes?: string;
  public_comments?: string;
}

// User interfaces
export interface CompanyUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  job_title?: string;
  phone_number?: string;
  role?: string;
  roles?: string[];
  last_login?: string;
}

// Feedback interfaces
export interface FeedbackSubmitData {
  feedbackType: string;
  feedback: string;
  rating?: number;
}

// Waitlist interfaces
export interface WaitlistSubmitData {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  role?: string;
  teamSize?: string;
  comments?: string;
}

/**
 * Standard API success response envelope
 */
export interface ApiResponse<T> {
  data: T;
  meta?: {
    [key: string]: unknown;
    pagination?: {
      next_cursor?: string;
      prev_cursor?: string;
      total?: number;
    };
    warnings?: string[];
    request_id?: string;
  };
}

/**
 * RFC 7807 Problem Details
 * @see https://datatracker.ietf.org/doc/html/rfc7807
 */
export interface Problem {
  /** URI reference identifying the problem type */
  type: string;

  /** Short, human-readable summary */
  title: string;

  /** HTTP status code */
  status: number;

  /** Machine-readable error code */
  code: string;

  /** Human-readable explanation specific to this occurrence */
  detail: string;

  /** URI reference identifying the specific occurrence */
  instance?: string;

  /** Request ID for correlation with backend logs */
  request_id?: string;

  /** Company context (multi-tenant) */
  company_id?: string;

  /** Field-level validation errors */
  errors?: Record<string, string[]>;

  /** Additional problem-specific context */
  metadata?: Record<string, unknown>;
}

/**
 * Type guard to check if error is a Problem
 */
export function isProblem(error: unknown): error is Problem {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    'status' in error &&
    'code' in error &&
    'detail' in error
  );
}

/**
 * Extract the first error message from field errors
 */
export function getFirstFieldError(problem: Problem, field: string): string | undefined {
  return problem.errors?.[field]?.[0];
}

/**
 * Check if Problem is a validation error (422)
 */
export function isValidationError(error: unknown): error is Problem {
  return isProblem(error) && error.status === 422;
}

/**
 * Check if Problem is a not found error (404)
 */
export function isNotFoundError(error: unknown): error is Problem {
  return isProblem(error) && error.status === 404;
}

/**
 * Check if Problem is a permission error (403)
 */
export function isPermissionError(error: unknown): error is Problem {
  return isProblem(error) && error.status === 403;
}

/**
 * Check if Problem is a conflict error (409)
 */
export function isConflictError(error: unknown): error is Problem {
  return isProblem(error) && error.status === 409;
}

/**
 * Offer Comment interfaces for sticky notes with free-form positioning
 */
export interface CommentReply {
  id: string;
  content: string;
  author_type: 'candidate' | 'recruiter';
  author_name: string;
  is_negotiation: boolean;
  created_at: string;
}

export interface OfferComment {
  id: string;
  offer_id: string;
  content: string;
  author_type: 'candidate' | 'recruiter';
  author_name: string;
  is_negotiation: boolean;
  position_x: number;
  position_y: number;
  is_resolved: boolean;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_at: string;
  updated_at: string;
  replies: CommentReply[];
}

export interface CreateCommentData {
  content: string;
  is_negotiation?: boolean;
  author_name?: string;
  position_x?: number;
  position_y?: number;
  parent_id?: string;
}

/**
 * Notifications inbox interfaces
 */
export interface UnreadCommentSummary {
  offer_id: string;
  role: string;
  candidate_name: string;
  candidate_email: string;
  unread_count: number;
  total_count: number;
  latest_comment: string | null;
  latest_comment_at: string | null;
  has_negotiation: boolean;
}

export interface NotificationsInbox {
  items: UnreadCommentSummary[];
  total_unread: number;
}

/**
 * Offer status types and filtering
 */
export const OFFER_STATUSES = [
  'unpublished',
  'published',
  'viewed',
  'accepted',
  'declined',
  'expired',
  'reneged',
  'rescinded',
] as const;

export type OfferStatus = (typeof OFFER_STATUSES)[number];

/**
 * Status groups for filtering
 */
export const PENDING_STATUSES: OfferStatus[] = ['unpublished', 'published', 'viewed'];

/**
 * Status counts interface for offer filtering
 */
export interface StatusCounts {
  all: number;
  accepted: number;
  pending: number;
  declined: number;
  expired: number;
  reneged: number;
  rescinded: number;
}

/**
 * Interface for offer items with required filtering fields
 */
export interface OfferFilterableItem {
  status: string;
  candidate: {
    name: string;
  };
  department?: string;
  salary: number | string;
  company?: {
    name: string;
  };
}

/**
 * Offer list item (for user offers)
 */
export interface OfferListItem extends OfferFilterableItem {
  id: string;
  candidate: {
    name: string;
    email: string;
  };
  role: string;
  status: string;
  expires_at: string | null;
  last_viewed_at: string | null;
  published_at?: string | null;
  accepted_at?: string | null;
  declined_at?: string | null;
  created_at: string;
  updated_at: string;
  employment_type?: EmploymentType;
  compensation_type?: CompensationType;
  hourly_rate?: number;
  end_date?: string;
}

/**
 * Offer list item with company info (for all offers)
 */
export interface OfferListItemWithCompany extends OfferListItem {
  company: {
    id: string;
    name: string;
  };
}
